import Accelerate from 'accelerate.js';
import * as SUGA from 'suga';

/**
 * Wheel visualization of the data that model contains.
 */
export default class WheelView extends PIXI.Container
    {
    /**
     * Constructs the object.
     *
     * @param  {String}  url         The url
     * @param  {Array}   scales      The scales of the 3 wheels
     * @param  {Number}  sliceCount  The slice count
     */
    constructor(url, scales, sliceCount)
    {
        super();

        // Set some constants
        this.PIE_ANGLE = 360 / sliceCount;
        const PIE_MIDDLE = this.PIE_ANGLE / 2;
        const PIE_OFFSET = 90 - PIE_MIDDLE;

        // Get the wheel texture
        const texture = new PIXI.Texture.fromImage(url);

        // Build the container and place it
        const container = new PIXI.Container();

        this.addChild(container);
        container.x = texture.width / 2;
        container.y = texture.height / 2;

        // Build wheels
        this.containerOuter = this.makeContainer(texture, scales[0], 0, -PIE_OFFSET);
        this.containerMiddle = this.makeContainer(texture, scales[1], 1, -PIE_OFFSET);
        this.containerInner = this.makeContainer(texture, scales[2], 2, -PIE_OFFSET);

        // Add wheels
        container.addChild(this.containerOuter);
        container.addChild(this.containerMiddle);
        container.addChild(this.containerInner);

        this.acceleratorOuter = new Accelerate(0, { allowOverride: true, debug: false });
        this.acceleratorMiddle = new Accelerate(0, { allowOverride: true });
        this.acceleratorInner = new Accelerate(0, { allowOverride: true });

        this.ticker = (delta) =>
        {
            this.containerOuter.wheel.rotation = window.degToRad(this.acceleratorOuter.advance(delta));
            this.containerMiddle.wheel.rotation = window.degToRad(this.acceleratorMiddle.advance(delta));
            this.containerInner.wheel.rotation = window.degToRad(this.acceleratorInner.advance(delta));
        };
    }

    /**
     * Set wheels back to initial position
     */
    reset()
    {
        this.acceleratorOuter.setPosition(0);
        this.acceleratorMiddle.setPosition(0);
        this.acceleratorInner.setPosition(0);
        this.containerOuter.wheel.rotation = 0;
        this.containerMiddle.wheel.rotation = 0;
        this.containerInner.wheel.rotation = 0;
    }

    /**
     * Spin all the wheels
     *
     * @param  {Object}    model              The model
     * @param  {Function}  maxSpeedHandler    The handler when maximum speed is reached
     * @param  {Function}  completionHandler  The completion handler
     */
    spin(model, completionHandler)
    {
        this.model = model;
        this.spinning = true;
        this.completionHandler = completionHandler;
        this.addAccelerators();

        this.startAllWheels(this.model.wheels)
            .then(() =>
                this.stopAllWheels(this.model.wheels, false));
    }

    /**
     * Starts all the wheels spinning
     *
     * @param  {Array}    wheels - The wheels
     * @return {Promise}  Completion Handler for all wheels
     */
    startAllWheels(wheels)
    {
        const p1 = this.startWheel(this.acceleratorInner, this.containerInner, wheels[0]);
        const p2 = this.startWheel(this.acceleratorMiddle, this.containerMiddle, wheels[1]);
        const p3 = this.startWheel(this.acceleratorOuter, this.containerOuter, wheels[2]);

        return Promise.all([p1, p2, p3]);// .then(cb)
    }

    /**
     * Starts a wheel.
     *
     * @param  {Accelerator}     accelerator  The accelerator
     * @param  {PIXI.Container}  container    The container
     * @param  {Number}          model        The delay
     * @return {Promise}         The motion promise chain
     */
    startWheel(accelerator, container, model)
    {
        const duration = model.startDuration;
        const delay = model.startDelay;

        container.stopped = false;

        return accelerator.wait(delay).then(() =>
            accelerator.accelerate(1100, duration, null))
            .then(() =>
            {
                accelerator.setPosition(this.PIE_ANGLE * model.sliceIndex);
            });
    }

    /**
     * Slow all the wheels down
     *
     * @param  {Array}    wheels      - The wheels
     * @param  {Number}   timeToStop  - The time to stop
     * @param  {Number}   revs        - The revolutions
     * @param  {Number}   bounceBack  - The bounce back
     * @return {Promise}  Promise when all are completed
     */
    stopAllWheels(wheels, instant)
    {
        if (!this.spinning) { return null; }

        this.isSlowingDown = true;
        const pDown1 = this.stopWheel(this.acceleratorInner, this.containerInner, wheels[0], instant);
        const pDown2 = this.stopWheel(this.acceleratorMiddle, this.containerMiddle, wheels[1], instant);
        const pDown3 = this.stopWheel(this.acceleratorOuter, this.containerOuter, wheels[2], instant);

        Promise.all([pDown1, pDown2, pDown3]).then(() => {
            this.completed()
        });
    }

    /**
     * Slow a wheel down
     *
     * @param  {Object}          model        - The model
     * @param  {Number}          timeToStop   - The time to stop
     * @param  {Number}          revolutions  - The revolutions
     * @param  {PIXI.Container}  container    - The container
     * @param  {Accelerate}      accelerator  - The accelerator
     * @return {Promise}         The motion promise chain
     */
    stopWheel(accelerator, container, model, instant)
    {
        if (container.stopped) { return; }

        accelerator.kill(false);

        const finalVelocity = 0;
        const bounceDegrees = model.bounceDegrees;
        const bounceDuration = model.bounceDuration;
        const delay = model.stopDelay;

        return accelerator.wait(delay)
            .then(() =>
            {
                const prizeAngle = this.PIE_ANGLE * model.sliceIndex;
                const currentAngle = accelerator.getPosition() % 360;
                let degreesToPrize = prizeAngle - currentAngle;

                if (degreesToPrize < 0) { degreesToPrize += 360; }

                const duration = model.stopDuration;
                const degreesToAdd = 360 * model.stopRevolutions;
                const degreesToEnd = bounceDegrees + degreesToAdd + degreesToPrize;
                // offset wheels to prize

                return accelerator.accelerate(finalVelocity, duration, degreesToEnd);
            })
            .then(() =>
                accelerator.accelerate(finalVelocity, bounceDuration, -bounceDegrees))
            .then(() =>
            {
                container.stopped = true;

                if (model.isSas) { return; }
                const i = 3 - container.index;

                SUGA.Sound.play(`stop-${i}`);

                return;
            });
    }

    /**
     * Stops all the wheels (this is the user action stop)
     *
     * @return {Promise}  Completion handler for all wheels
     */
    stop()
    {
        this.acceleratorInner.setPosition(this.PIE_ANGLE * this.model.wheels[0].sliceIndex);
        this.acceleratorMiddle.setPosition(this.PIE_ANGLE * this.model.wheels[1].sliceIndex);
        this.acceleratorOuter.setPosition(this.PIE_ANGLE * this.model.wheels[2].sliceIndex);

        this.calculateStoppingDistance(this.acceleratorInner, this.model.wheels[0]);
        this.calculateStoppingDistance(this.acceleratorMiddle, this.model.wheels[1]);
        this.calculateStoppingDistance(this.acceleratorOuter, this.model.wheels[2]);

        return this.stopAllWheels(this.model.wheels, true);
    }

    calculateStoppingDistance(accelerator, model)
    {
        const v0 = accelerator.getVelocity();
        const maxRevolutions = Math.floor(v0 / 360);

        model.isSas = false;
        model.stopDelay = 0;
        model.stopDuration = 0.5;
        model.stopRevolutions = 0;
        model.bounceDegrees = maxRevolutions;
        model.bounceDuration = 0.2;
    }

    /**
     * When the animation is complete
     */
    completed()
    {
        this.completionHandler();
        this.removeAccelerators();
        this.isSlowingDown = false;
        this.spinning = false;
    }

    /**
     * Adds accelerators.
     */
    addAccelerators()
    {
        // Create accelerators
        // this.acceleratorOuter = new Accelerate(0);
        // this.acceleratorMiddle = new Accelerate(0);
        // this.acceleratorInner = new Accelerate(0);
        window.__ticker.add(this.ticker);
    }

    /**
     * Removes accelerators.
     */
    removeAccelerators()
    {
        // this.acceleratorOuter = null;
        // this.acceleratorMiddle = null;
        // this.acceleratorInner = null;
        window.__ticker.remove(this.ticker);
    }

    /**
     * Makes a container.
     *
     * @param  {PIXI.Texture}    texture     The texture
     * @param  {Number}          scale       The scale
     * @param  {number}          wheelIndex  The wheel index
     * @param  {number}          offset      The offset
     * @return {PIXI.Container}  The Wheel
     */
    makeContainer(texture, scale, wheelIndex, offset)
    {
        const container = new PIXI.Container();

        container.pivot.x = 0;
        container.pivot.y = 0;
        container.scale.set(scale);
        container.index = wheelIndex;
        container.stopped = true;

        const wheel = new PIXI.Sprite(texture);

        container.addChild(wheel);
        container.wheel = wheel;
        wheel.anchor.set(0.5);

        return container;
    }
}



// WEBPACK FOOTER //
// ./themes/fq/view/wheel-view.js